import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { getAlpacaStatus, getMe, logout } from './api';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import RegisterPage from './pages/RegisterPage';
import ConnectPage from './pages/ConnectPage';
import DashboardPage from './pages/DashboardPage';
import ProposalsPage from './pages/ProposalsPage';
import OrdersPage from './pages/OrdersPage';
import ChatPage from './pages/ChatPage';
import PolicyPage from './pages/PolicyPage';

import PreferencesPage from './pages/PreferencesPage';
import AllocationPage from './pages/AllocationPage';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const NAV = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/chat', label: 'Market Assistant', icon: '💬' },
  { path: '/proposals', label: 'Order Review', icon: '📋' },
  { path: '/orders', label: 'Order History', icon: '📜' },
  { path: '/preferences', label: 'Preferences', icon: '⚙️' },
  { path: '/connect', label: 'Brokerage', icon: '🔗' },
];

function Sidebar({ alpacaOk, user, onLogout }) {
  const loc = useLocation();
  return (
    <aside style={lay.sidebar}>
      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-start',padding:'8px 10px 14px',marginBottom:12,borderBottom:'1px solid var(--border)'}}>
        <div style={{width:48,height:48,flexShrink:0}}>
          <img src="/logo_256.png" alt="TM" style={{width:'100%',height:'100%',objectFit:'contain'}} />
        </div>
        <div style={{fontSize:17,fontWeight:800,color:'var(--t1)',marginTop:4,letterSpacing:0.5}}>Thankful Money</div>
        <div style={{fontSize:11,color:'var(--t3)',marginTop:1}}>AI Market Analysis</div>
      </div>
      <nav style={lay.nav}>
        {NAV.map(n => {
          const active = loc.pathname === n.path;
          return (
            <Link key={n.path} to={n.path} style={{
              ...lay.navItem,
              color: active ? 'var(--green)' : 'var(--t3)',
              background: active ? 'var(--green-bg)' : 'transparent',
            }}>
              <span style={{ fontSize: 15 }}>{n.icon}</span>
              <span>{n.label}</span>
            </Link>
          );
        })}
      </nav>
      <div style={lay.sideFooter}>
        {user && (
          <div style={lay.userInfo}>
            <div style={lay.avatar}>{(user.name || 'U')[0].toUpperCase()}</div>
            <div style={{flex:1}}>
              <div style={lay.userName}>{user.name}</div>
              <div style={lay.userId}>{user.email || user.user_id}</div>
            </div>
            <button onClick={onLogout} style={lay.logoutBtn} title="Sign out">↪</button>
          </div>
        )}
        <div style={{
          ...lay.envTag,
          background: alpacaOk ? 'var(--green-bg)' : 'var(--amber-bg)',
          color: alpacaOk ? 'var(--green)' : 'var(--amber)',
        }}>
          {alpacaOk ? '● CONNECTED' : '○ NOT CONNECTED'}
        </div>
        <div style={lay.envLabel}>PAPER TRADING MODE</div>
      </div>
    </aside>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [alpaca, setAlpaca] = useState(null);
  const [loading, setLoading] = useState(true);
  const loc = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    getMe()
      .then(data => {
        if (data.authenticated) {
          setUser(data.user);
          return getAlpacaStatus()
            .then(setAlpaca)
            .catch(() => setAlpaca({ connected: false }));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const refreshAlpaca = () => {
    getAlpacaStatus().then(setAlpaca).catch(() => setAlpaca({ connected: false }));
  };

  const handleLogin = (userData) => {
    setUser(userData);
    setLoading(true);
    getAlpacaStatus()
      .then(a => {
        setAlpaca(a);
        navigate(a?.connected ? '/dashboard' : '/connect', { replace: true });
      })
      .catch(() => {
        setAlpaca({ connected: false });
        navigate('/connect', { replace: true });
      })
      .finally(() => setLoading(false));
  };

  const handleLogout = async () => {
    try { await logout(); } catch {}
    setUser(null);
    setAlpaca(null);
    setLoading(false);
  };

  const policyRoutes = (
    <Routes>
      <Route path="/terms" element={<PolicyPage type="terms" />} />
      <Route path="/privacy" element={<PolicyPage type="privacy" />} />
      <Route path="/pricing" element={<PolicyPage type="pricing" />} />
      <Route path="/cybersecurity" element={<PolicyPage type="cybersecurity" />} />
    </Routes>
  );

  const isPolicyRoute = ['/terms', '/privacy', '/pricing', '/cybersecurity'].includes(loc.pathname);
  const isPublicRoute = ['/', '/login', '/register'].includes(loc.pathname);

  if (isPolicyRoute) {
    return (
      <div style={lay.publicWrapper}>
        <main style={lay.publicMain}>{policyRoutes}</main>
      </div>
    );
  }

  // Show splash while checking auth OR while alpaca status is resolving
  if (loading || (user && alpaca === null)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-0)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, margin: '0 auto 16px' }}>
            <img src="/logo_256.png" alt="TM" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)', marginBottom: 4 }}>Thankful Money</div>
          <div style={{ fontSize: 12, color: 'var(--t3)' }}>Loading...</div>
        </div>
      </div>
    );
  }

  // Not logged in — show public routes
  if (!user) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/terms" element={<PolicyPage type="terms" />} />
          <Route path="/privacy" element={<PolicyPage type="privacy" />} />
          <Route path="/pricing" element={<PolicyPage type="pricing" />} />
          <Route path="/cybersecurity" element={<PolicyPage type="cybersecurity" />} />
          <Route path="*" element={<LandingPage />} />
        </Routes>
      </GoogleOAuthProvider>
    );
  }

  const connected = alpaca?.connected;

  return (
    <div style={lay.wrapper}>
      <Sidebar alpacaOk={connected} user={user} onLogout={handleLogout} />
      <main style={lay.main}>
        <Routes>
          <Route path="/" element={<Navigate to={connected ? '/dashboard' : '/connect'} replace />} />
          <Route path="/connect" element={<ConnectPage alpaca={alpaca} onRefresh={refreshAlpaca} />} />
          <Route path="/dashboard" element={connected ? <DashboardPage alpaca={alpaca} /> : <Navigate to="/connect" replace />} />
          <Route path="/chat" element={connected ? <ChatPage /> : <Navigate to="/connect" replace />} />
          <Route path="/proposals" element={connected ? <ProposalsPage /> : <Navigate to="/connect" replace />} />
          <Route path="/orders" element={connected ? <OrdersPage /> : <Navigate to="/connect" replace />} />
          <Route path="/preferences" element={connected ? <PreferencesPage /> : <Navigate to="/connect" replace />} />
          <Route path="/allocation" element={connected ? <AllocationPage /> : <Navigate to="/connect" replace />} />
        </Routes>
      </main>
    </div>
  );
}

const lay = {
  wrapper: { display: 'flex', minHeight: '100vh' },
  publicWrapper: { minHeight: '100vh', background: 'var(--bg-0)', color: 'var(--t1)' },
  publicMain: { maxWidth: 980, margin: '0 auto', padding: '42px 28px' },
  sidebar: {
    width: 270, background: 'var(--bg-1)', borderRight: '1px solid var(--border)',
    display: 'flex', flexDirection: 'column', padding: '24px 14px',
    position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 10,
  },
  brand: { display: 'flex', alignItems: 'center', gap: 14, padding: '0 10px', marginBottom: 36 },
  logoBox: {
    width: 38, height: 38, borderRadius: 10, background: 'var(--green)',
    color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: 14, flexShrink: 0,
  },
  logoName: { fontSize: 15, fontWeight: 700, color: 'var(--t1)' },
  logoSub: { fontSize: 11, color: 'var(--t3)', marginTop: 1 },
  nav: { display: 'flex', flexDirection: 'column', gap: 2 },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
    borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: 'none',
    transition: 'all 0.12s',
  },
  badge: {
    marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: 'var(--amber)',
    background: 'var(--amber-bg)', padding: '1px 8px', borderRadius: 10,
  },
  sideFooter: { marginTop: 'auto', padding: '0 10px', display: 'flex', flexDirection: 'column', gap: 8 },
  userInfo: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '10px 0', borderTop: '1px solid var(--border)', paddingTop: 16 },
  avatar: {
    width: 32, height: 32, borderRadius: '50%', background: 'var(--bg-3)',
    color: 'var(--t2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, flexShrink: 0,
  },
  userName: { fontSize: 13, fontWeight: 600, color: 'var(--t1)' },
  userId: { fontSize: 11, color: 'var(--t3)' },
  logoutBtn: {
    background: 'none', border: 'none', color: 'var(--t3)', fontSize: 16,
    cursor: 'pointer', padding: '4px 6px', borderRadius: 4, flexShrink: 0,
  },
  envTag: {
    fontSize: 11, fontWeight: 600, letterSpacing: 1, padding: '6px 0',
    borderRadius: 6, textAlign: 'center',
  },
  envLabel: {
    fontSize: 10, fontWeight: 600, letterSpacing: 1.5, color: 'var(--t3)', textAlign: 'center',
  },
  main: { flex: 1, marginLeft: 270, padding: '32px 44px', maxWidth: 1000 },
};
